<template>
  <div
    v-if="authStore.viewingAs || authStore.viewAsFailed"
    class="view-as-banner"
    :class="{ 'view-as-banner--failed': authStore.viewAsFailed }"
    role="status"
    data-testid="view-as-banner"
  >
    <v-icon :icon="authStore.viewAsFailed ? mdiAlertCircle : mdiAccountEye" size="20" class="view-as-banner__icon" />

    <!-- Deliberately does not assert whose account is on screen: this state covers both a
         swap that did not apply (you are still the admin) and a session left over from
         before #2692 (you are the target). Only "not active" is true of both. -->
    <span v-if="authStore.viewAsFailed" class="view-as-banner__text" data-testid="view-as-banner-failed">
      This view-as session is <strong>not active</strong
      ><span v-if="authStore.viewAsTargetEmail"> for {{ authStore.viewAsTargetEmail }}</span
      >. Writes are disabled until you return to your admin account{{
        authStore.viewAsAdminEmail ? ` (${authStore.viewAsAdminEmail})` : ""
      }}.
    </span>
    <span v-else class="view-as-banner__text">
      Viewing as <strong>{{ authStore.userName || "this user" }}</strong>
      <span class="view-as-banner__identity">({{ targetIdentity }})</span> as admin
      {{ authStore.viewAsAdminEmail }} (read-only). Writes are disabled.
    </span>

    <v-btn
      variant="flat"
      size="small"
      color="white"
      class="view-as-banner__btn"
      :prepend-icon="mdiArrowLeft"
      :loading="stopping"
      data-testid="view-as-return-btn"
      @click="returnToAdmin"
    >
      Return to admin
    </v-btn>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { mdiAccountEye, mdiAlertCircle, mdiArrowLeft } from "@mdi/js";
import { useAuthStore } from "@/store/auth";
import { handleApiError } from "@/core/api/handleApiError";

const authStore = useAuthStore();
const stopping = ref(false);

// Display names are not unique, so a name alone cannot tell an admin whether the
// swap landed on the account they picked. Email plus id is what makes the session
// identifiable. See `docs/spec/admin/read-only-view-as.md` §5.
const targetIdentity = computed(() => {
  const email = authStore.viewAsTargetEmail;
  const id = authStore.viewAsTargetId;
  return id === undefined ? email : `${email}, #${id}`;
});

async function returnToAdmin(): Promise<void> {
  stopping.value = true;
  try {
    // Navigates away (full reload) on success, so `stopping` is not reset here.
    await authStore.stopViewAs();
  } catch (error) {
    stopping.value = false;
    handleApiError(error, { fallbackMessage: "Unable to return to your admin account" });
  }
}
</script>

<style scoped>
.view-as-banner {
  position: sticky;
  top: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: rgb(var(--v-theme-warning));
  color: #fff;
  font-size: 0.9rem;
  box-shadow: var(--th-shadow-card, 0 2px 8px rgba(0, 0, 0, 0.15));
}

.view-as-banner--failed {
  background: rgb(var(--v-theme-error));
}

.view-as-banner__icon {
  flex: 0 0 auto;
}

.view-as-banner__text {
  flex: 1 1 auto;
}

.view-as-banner__identity {
  margin-left: 4px;
  opacity: 0.85;
}

.view-as-banner__btn {
  flex: 0 0 auto;
  color: rgb(var(--v-theme-warning)) !important;
}

.view-as-banner--failed .view-as-banner__btn {
  color: rgb(var(--v-theme-error)) !important;
}
</style>
